/**
 * Demo Nodes for Testing
 * Creates 15 diverse nodes with realistic configurations
 */

import { GateMeshNode, NodeType, MeshRole, NodeState } from '@/types/agriculture';

export function createDemoNodes(): GateMeshNode[] {
  const now = Date.now();

  return [
    // ========================================
    // WATER MANAGEMENT (5 nodes)
    // ========================================
    {
      id: 'demo-node-001',
      userGivenName: 'Reservoir Level Monitor',
      hardwareId: 'ESP32-S3-001',
      nodeTypes: [NodeType.WATER_LEVEL_SENSOR],
      state: NodeState.ACTIVE,
      battery: 92,
      rssi: -52,
      meshRole: MeshRole.ROUTER,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-north',
      zoneId: 'zone-1',
      location: {
        lat: 40.7128,
        lng: -74.0060,
      },
      detectedSensors: [
        { type: 'ultrasonic', model: 'HC-SR04', port: 'D2', name: 'Ultrasonic Distance' },
        { type: 'temperature', model: 'DS18B20', port: 'D3', name: 'Water Temperature' },
      ],
      config: {
        reportingInterval: 300,
      },
      createdAt: now - 86400000 * 30,
      updatedAt: now,
    },
    {
      id: 'demo-node-002',
      userGivenName: 'Headgate Controller #1',
      hardwareId: 'ESP32-S3-002',
      nodeTypes: [NodeType.HEADGATE_CONTROLLER, NodeType.GATE_VALVE],
      state: NodeState.ACTIVE,
      battery: 78,
      rssi: -58,
      meshRole: MeshRole.ROUTER,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-north',
      zoneId: 'zone-1',
      location: {
        lat: 40.7135,
        lng: -74.0055,
      },
      config: {
        reportingInterval: 300,
        valvePosition: 75,
      },
      createdAt: now - 86400000 * 28,
      updatedAt: now,
    },
    {
      id: 'demo-node-003',
      userGivenName: 'Flow Sensor Alpha',
      hardwareId: 'ESP32-S3-003',
      nodeTypes: [NodeType.FLOW_SENSOR],
      state: NodeState.ACTIVE,
      battery: 84,
      rssi: -61,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-north',
      zoneId: 'zone-1',
      location: {
        lat: 40.7140,
        lng: -74.0050,
      },
      detectedSensors: [
        { type: 'flow', model: 'YF-S201', port: 'D4', name: 'Water Flow' },
      ],
      config: {
        reportingInterval: 300,
      },
      createdAt: now - 86400000 * 25,
      updatedAt: now,
    },
    {
      id: 'demo-node-004',
      userGivenName: 'Pump Station #1',
      hardwareId: 'ESP32-S3-004',
      nodeTypes: [NodeType.PUMP_CONTROLLER, NodeType.PUMP_RELAY],
      state: NodeState.ACTIVE,
      battery: 88,
      rssi: -55,
      meshRole: MeshRole.ROUTER,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-north',
      zoneId: 'zone-2',
      location: {
        lat: 40.7145,
        lng: -74.0045,
      },
      config: {
        reportingInterval: 300,
        pumpEnabled: true,
      },
      createdAt: now - 86400000 * 27,
      updatedAt: now,
    },
    {
      id: 'demo-node-005',
      userGivenName: 'Pressure Monitor #1',
      hardwareId: 'ESP32-S3-005',
      nodeTypes: [NodeType.PRESSURE_SENSOR],
      state: NodeState.ACTIVE,
      battery: 76,
      rssi: -63,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-north',
      zoneId: 'zone-2',
      location: {
        lat: 40.7150,
        lng: -74.0040,
      },
      detectedSensors: [
        { type: 'pressure', model: 'BMP280', port: 'I2C', name: 'Line Pressure', address: 0x76 },
      ],
      config: {
        reportingInterval: 300,
      },
      createdAt: now - 86400000 * 24,
      updatedAt: now,
    },

    // ========================================
    // FIELD MONITORING (5 nodes)
    // ========================================
    {
      id: 'demo-node-006',
      userGivenName: 'Soil Moisture A1',
      hardwareId: 'ESP32-S3-006',
      nodeTypes: [NodeType.SOIL_MOISTURE_SENSOR],
      state: NodeState.ACTIVE,
      battery: 81,
      rssi: -59,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-south',
      zoneId: 'zone-3',
      location: {
        lat: 40.7120,
        lng: -74.0065,
      },
      detectedSensors: [
        { type: 'soil_moisture', model: 'Capacitive', port: 'A0', name: 'Soil Moisture' },
        { type: 'temperature', model: 'DHT22', port: 'D5', name: 'Air Temperature' },
      ],
      config: {
        reportingInterval: 600,
      },
      createdAt: now - 86400000 * 20,
      updatedAt: now,
    },
    {
      id: 'demo-node-007',
      userGivenName: 'Soil Moisture A2',
      hardwareId: 'ESP32-S3-007',
      nodeTypes: [NodeType.SOIL_MOISTURE_SENSOR],
      state: NodeState.ACTIVE,
      battery: 73,
      rssi: -67,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-south',
      zoneId: 'zone-3',
      location: {
        lat: 40.7115,
        lng: -74.0070,
      },
      detectedSensors: [
        { type: 'soil_moisture', model: 'Capacitive', port: 'A0', name: 'Soil Moisture' },
      ],
      config: {
        reportingInterval: 600,
      },
      createdAt: now - 86400000 * 18,
      updatedAt: now,
    },
    {
      id: 'demo-node-008',
      userGivenName: 'Weather Station',
      hardwareId: 'ESP32-S3-008',
      nodeTypes: [NodeType.WEATHER_STATION],
      state: NodeState.ACTIVE,
      battery: 95,
      rssi: -48,
      solar: 5.2,
      isCharging: true,
      meshRole: MeshRole.ROUTER,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-south',
      zoneId: 'zone-3',
      location: {
        lat: 40.7125,
        lng: -74.0062,
      },
      detectedSensors: [
        { type: 'temperature', model: 'BME280', port: 'I2C', name: 'Temperature', address: 0x77 },
        { type: 'humidity', model: 'BME280', port: 'I2C', name: 'Humidity', address: 0x77 },
        { type: 'pressure', model: 'BME280', port: 'I2C', name: 'Barometric Pressure', address: 0x77 },
      ],
      config: {
        reportingInterval: 900,
      },
      createdAt: now - 86400000 * 35,
      updatedAt: now,
    },
    {
      id: 'demo-node-009',
      userGivenName: 'Lateral Valve L1',
      hardwareId: 'ESP32-S3-009',
      nodeTypes: [NodeType.LATERAL_VALVE],
      state: NodeState.IDLE,
      battery: 69,
      rssi: -71,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-south',
      zoneId: 'zone-4',
      location: {
        lat: 40.7110,
        lng: -74.0075,
      },
      config: {
        reportingInterval: 300,
        valvePosition: 0,
      },
      createdAt: now - 86400000 * 22,
      updatedAt: now,
    },
    {
      id: 'demo-node-010',
      userGivenName: 'Lateral Valve L2',
      hardwareId: 'ESP32-S3-010',
      nodeTypes: [NodeType.LATERAL_VALVE],
      state: NodeState.ACTIVE,
      battery: 72,
      rssi: -68,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-south',
      zoneId: 'zone-4',
      location: {
        lat: 40.7105,
        lng: -74.0080,
      },
      config: {
        reportingInterval: 300,
        valvePosition: 50,
      },
      createdAt: now - 86400000 * 21,
      updatedAt: now,
    },

    // ========================================
    // EAST SECTION (3 nodes)
    // ========================================
    {
      id: 'demo-node-011',
      userGivenName: 'Section Controller East',
      hardwareId: 'ESP32-S3-011',
      nodeTypes: [NodeType.SECTION_CONTROLLER],
      state: NodeState.ACTIVE,
      battery: 86,
      rssi: -54,
      meshRole: MeshRole.ROUTER,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-east',
      zoneId: 'zone-5',
      location: {
        lat: 40.7130,
        lng: -74.0030,
      },
      config: {
        reportingInterval: 300,
      },
      createdAt: now - 86400000 * 26,
      updatedAt: now,
    },
    {
      id: 'demo-node-012',
      userGivenName: 'Variable Valve E1',
      hardwareId: 'ESP32-S3-012',
      nodeTypes: [NodeType.VARIABLE_VALVE],
      state: NodeState.ACTIVE,
      battery: 79,
      rssi: -62,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-east',
      zoneId: 'zone-5',
      location: {
        lat: 40.7132,
        lng: -74.0028,
      },
      config: {
        reportingInterval: 300,
        valvePosition: 85,
      },
      createdAt: now - 86400000 * 19,
      updatedAt: now,
    },
    {
      id: 'demo-node-013',
      userGivenName: 'Flow Sensor Beta',
      hardwareId: 'ESP32-S3-013',
      nodeTypes: [NodeType.FLOW_SENSOR],
      state: NodeState.ACTIVE,
      battery: 82,
      rssi: -60,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now,
      farmId: 'farm-1',
      fieldId: 'field-east',
      zoneId: 'zone-5',
      location: {
        lat: 40.7135,
        lng: -74.0025,
      },
      detectedSensors: [
        { type: 'flow', model: 'YF-S201', port: 'D4', name: 'Water Flow' },
      ],
      config: {
        reportingInterval: 300,
      },
      createdAt: now - 86400000 * 17,
      updatedAt: now,
    },

    // ========================================
    // OFFLINE/WARNING NODES (2 nodes)
    // ========================================
    {
      id: 'demo-node-014',
      userGivenName: 'Remote Sensor #1',
      hardwareId: 'ESP32-S3-014',
      nodeTypes: [NodeType.SOIL_MOISTURE_SENSOR],
      state: NodeState.IDLE,
      battery: 15,
      rssi: -89,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: true,
      lastSeen: now - 3600000,
      farmId: 'farm-1',
      fieldId: 'field-west',
      zoneId: 'zone-6',
      location: {
        lat: 40.7100,
        lng: -74.0090,
      },
      detectedSensors: [
        { type: 'soil_moisture', model: 'Capacitive', port: 'A0', name: 'Soil Moisture' },
      ],
      config: {
        reportingInterval: 600,
      },
      createdAt: now - 86400000 * 15,
      updatedAt: now - 3600000,
    },
    {
      id: 'demo-node-015',
      userGivenName: 'Gate Valve West',
      hardwareId: 'ESP32-S3-015',
      nodeTypes: [NodeType.GATE_VALVE],
      state: NodeState.OFFLINE,
      battery: 0,
      rssi: -99,
      meshRole: MeshRole.CLIENT,
      firmwareVersion: '1.0.0',
      isOnline: false,
      lastSeen: now - 86400000,
      farmId: 'farm-1',
      fieldId: 'field-west',
      zoneId: 'zone-6',
      location: {
        lat: 40.7095,
        lng: -74.0095,
      },
      config: {
        reportingInterval: 300,
        valvePosition: 0,
      },
      createdAt: now - 86400000 * 14,
      updatedAt: now - 86400000,
    },
  ];
}
